import React from "react";
import { Iconography, Loader, LoaderSizes } from "@hid-galaxy-ui/galaxy-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Checkbox,
  FormControl,
  ListItemText,
  MenuItem,
  Select,
  Box,
} from "@mui/material";
import sanitizeHtml from "sanitize-html";
import { axiosRestInstance } from "../../../common/axios";
import { useAppDispatch, useAppSelector } from "../../../redux";
import { getFeaturesSagaAction, getFeatureTechnologyOptionsSagaAction } from "../../../redux/saga/feature-management/saga-actions";
import {
  extractNumericId,
  getCategoriesSagaAction,
} from "../../../redux/saga/project-validation/saga-actions";
import { FeatureSupportApi, FeatureTechnologyOption } from "../../../redux/slices/feature-management/Types";
import { RequirementType } from "../../../redux/slices/project-validation/Types";
import "./FeatureComparisonTable.scss";

type ComparisonCell = {
  support: string;
  requirements: string[];
};

const resolveTechnologyDisplayName = (
  technology: number | string,
  options: FeatureTechnologyOption[]
): string => {
  const asString = String(technology).trim();
  if (!asString) return "";

  if (/^\d+$/.test(asString)) {
    const byId = options.find((option) => option.id === Number(asString));
    if (byId) return byId.name;
  }

  const byName = options.find((option) => option.name === asString);
  return byName?.name || asString;
};

const getStatusClass = (value: string) => {
  const lower = value.toLowerCase();
  if (lower === "supported") return "status supported";
  if (lower === "not supported") return "status not-supported";
  return "status timeline";
};

const formatDateToDisplay = (value: string) => {
  const [year, month, day] = value.split("-");
  if (!year || !month || !day) return value;
  return `${day}/${month}/${year}`;
};

const formatRequirementId = (id: string) => {
  const trimmed = id.trim();
  if (trimmed.toUpperCase().startsWith("REQ-")) return trimmed.toUpperCase();
  return `REQ-${trimmed}`;
};

const formatSupport = (support: FeatureSupportApi): string => {
  if (support.type === "supported") return "Supported";
  if (support.type === "not_supported") return "Not Supported";
  if (support.config.mode === "fulldate") {
    return formatDateToDisplay(support.config.value);
  }
  return support.config.quarter
    ? `${support.config.quarter} ${String(support.config.value)}`
    : String(support.config.value);
};

const modalSanitizeOptions = {
  allowedTags: [
    "p",
    "br",
    "ul",
    "ol",
    "li",
    "strong",
    "b",
    "em",
    "i",
    "u",
    "s",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "a",
  ],
  allowedAttributes: { a: ["href", "target", "rel"] },
  allowedSchemes: ["http", "https", "mailto", "tel"],
};

const FeatureComparisonTable: React.FC = () => {
  const dispatch = useAppDispatch();
  const features = useAppSelector((state) => state.feature_management.features);
  const categories = useAppSelector((state) => state.project_validation.categories);
  const featuresLoading = useAppSelector(
    (state) => state.feature_management.status.featuresLoading
  );
  const technologyOptions = useAppSelector(
    (state) => state.feature_management.technologyOptions
  );
  const inactiveTechnologyOptions = useAppSelector(
    (state) => state.feature_management.inactiveTechnologyOptions
  );
  const technologyOptionsLoading = useAppSelector(
    (state) => state.feature_management.status.technologyOptionsLoading
  );
  const categoriesLoading = useAppSelector(
    (state) => state.project_validation.status.categoriesLoading
  );
  const [searchText, setSearchText] = React.useState("");
  const [selectedCategory, setSelectedCategory] = React.useState("all");
  const [visibleColumns, setVisibleColumns] = React.useState<string[]>([]);
  const [isDescriptionModalOpen, setIsDescriptionModalOpen] = React.useState(false);
  const [selectedDescriptions, setSelectedDescriptions] = React.useState({
    highLevelScenario: "",
    requirementDescription: "",
  });
  const [isRequirementModalOpen, setIsRequirementModalOpen] = React.useState(false);
  const [requirementModalLoading, setRequirementModalLoading] = React.useState(false);
  const [selectedRequirementDescription, setSelectedRequirementDescription] =
    React.useState("");

  const SelectIcon = (props: any) => (
    <Iconography icon="angleDown" className={`comparison-select-icon ${props.className || ""}`} />
  );

  React.useEffect(() => {
    dispatch(getCategoriesSagaAction());
    dispatch(getFeaturesSagaAction());
    dispatch(getFeatureTechnologyOptionsSagaAction());
  }, [dispatch]);

  const categoriesWithCounts = React.useMemo(() => {
    return categories
      .map((category) => ({
        id: String(category.ID),
        name: category.category_name,
        count: features.filter((feature) => feature.category_id === category.ID).length,
      }))
      .filter((category) => category.count > 0);
  }, [categories, features]);

  const categoryNameById = React.useMemo(() => {
    const map = new Map<number, string>();
    categories.forEach((item) => map.set(item.ID, item.category_name));
    return map;
  }, [categories]);

  const filteredFeatures = React.useMemo(() => {
    const base =
      selectedCategory === "all"
        ? features
        : features.filter((feature) => String(feature.category_id) === selectedCategory);

    if (!searchText.trim()) return base;
    const text = searchText.toLowerCase();
    return base.filter((feature) => feature.feature_name.toLowerCase().includes(text));
  }, [features, selectedCategory, searchText]);

  const legacyTechnologies = React.useMemo(() => {
    const apiTechnologyNames = new Set(
      technologyOptions.map((technology) => technology.name)
    );
    const inactiveTechnologyKeys = new Set(
      inactiveTechnologyOptions.flatMap((technology) => [
        String(technology.id),
        technology.name,
      ])
    );
    const seen = new Set<string>();
    const list: string[] = [];

    features.forEach((feature) => {
      (feature.technology_support || []).forEach((item) => {
        if (inactiveTechnologyKeys.has(String(item.technology).trim())) return;
        const technologyName = resolveTechnologyDisplayName(
          item.technology,
          technologyOptions
        );
        if (
          technologyName &&
          !apiTechnologyNames.has(technologyName) &&
          !inactiveTechnologyKeys.has(technologyName) &&
          !seen.has(technologyName)
        ) {
          seen.add(technologyName);
          list.push(technologyName);
        }
      });
    });

    return list;
  }, [features, technologyOptions, inactiveTechnologyOptions]);

  const availableTechnologies = React.useMemo(() => {
    return [
      ...technologyOptions.map((technology) => technology.name),
      ...legacyTechnologies,
    ];
  }, [technologyOptions, legacyTechnologies]);

  React.useEffect(() => {
    setVisibleColumns((prev) => {
      const validSelection = prev.filter((item) => availableTechnologies.includes(item));
      if (validSelection.length > 0) return validSelection;
      return availableTechnologies;
    });
  }, [availableTechnologies]);

  const tableRows = React.useMemo(() => {
    return filteredFeatures.map((feature) => {
      const cells = new Map<string, ComparisonCell>();
      (feature.technology_support || []).forEach((item) => {
        const technologyName = resolveTechnologyDisplayName(
          item.technology,
          technologyOptions
        );
        cells.set(technologyName, {
          support: formatSupport(item.support),
          requirements: item.requirement || [],
        });
      });
      return {
        id: feature.ID,
        featureName: feature.feature_name,
        scopeValue: feature.implementation_scope,
        highLevelScenario: feature.high_level_scenario || "",
        requirementDescription: feature.requirement_description || "",
        categoryName: categoryNameById.get(feature.category_id) || "",
        cells,
      };
    });
  }, [filteredFeatures, categoryNameById, technologyOptions]);

  const selectedCategoryLabel = React.useMemo(() => {
    if (selectedCategory === "all") return "All Categories";
    const found = categoriesWithCounts.find((item) => item.id === selectedCategory);
    return found ? found.name : "All Categories";
  }, [selectedCategory, categoriesWithCounts]);

  const handleColumnsChange = (value: string[]) => {
    setVisibleColumns(value);
  };

  const openDescriptionModal = (row: {
    highLevelScenario: string;
    requirementDescription: string;
  }) => {
    setSelectedDescriptions({
      highLevelScenario: row.highLevelScenario || "",
      requirementDescription: row.requirementDescription || "",
    });
    setIsDescriptionModalOpen(true);
  };

  const openRequirementModal = async (requirementId: string) => {
    setIsRequirementModalOpen(true);
    setRequirementModalLoading(true);
    setSelectedRequirementDescription("");

    try {
      const numericId = extractNumericId(requirementId);
      const response = await axiosRestInstance.get<{
        success: boolean;
        requirement: RequirementType | RequirementType[];
      }>(`/project/validation/requirement/${numericId}`);
      const data = response.data?.requirement;
      const requirement = Array.isArray(data) ? data[0] : data;
      setSelectedRequirementDescription(requirement?.requirement_description || "N/A");
    } catch {
      setSelectedRequirementDescription("Unable to load requirement details.");
    } finally {
      setRequirementModalLoading(false);
    }
  };

  const closeRequirementModal = () => {
    setIsRequirementModalOpen(false);
    setSelectedRequirementDescription("");
    setRequirementModalLoading(false);
  };

  return (
    <div className="feature-comparison-page">
      <div className="feature-comparison-card">
        <div className="card-header">
          <h2>Feature Comparison Table</h2>
          <p>Compare features across different platforms</p>
        </div>

        <div className="toolbar">
          <div className="search-box">
            <Iconography icon="magnifyingGlass" />
            <input
              placeholder="Search Feature by Name..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>
          <div className="toolbar-actions">
            <FormControl size="small" className="toolbar-select-wrap category-select-wrap">
              <Select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                IconComponent={SelectIcon}
                displayEmpty
                renderValue={() => (
                  <span className="category-select-value">
                    <Iconography icon="filter" />
                    <span>{selectedCategoryLabel}</span>
                  </span>
                )}
                className="toolbar-select"
              >
                <MenuItem value="all">All Categories</MenuItem>
                {categoriesWithCounts.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" className="toolbar-select-wrap columns-select-wrap">
              <Select
                multiple
                value={visibleColumns}
                onChange={(e) => handleColumnsChange(e.target.value as string[])}
                IconComponent={SelectIcon}
                renderValue={() => (
                  <span className="columns-select-value">
                    <span className="vertical-bars" aria-hidden>
                      <i />
                      <i />
                      <i />
                    </span>
                    <span>Columns</span>
                  </span>
                )}
                className="toolbar-select"
                MenuProps={{
                  PaperProps: { className: "columns-menu-paper" },
                }}
              >
                <MenuItem
                  disableRipple
                  disableTouchRipple
                  className="columns-menu-header"
                >
                  <span>Compare Columns</span>
                  <span className="menu-actions">
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        setVisibleColumns(availableTechnologies);
                      }}
                    >
                      All
                    </button>
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        setVisibleColumns([]);
                      }}
                    >
                      None
                    </button>
                  </span>
                </MenuItem>

                {availableTechnologies.map((column) => (
                  <MenuItem key={column} value={column}>
                    <Checkbox checked={visibleColumns.includes(column)} />
                    <ListItemText primary={column} />
                  </MenuItem>
                ))}

                <MenuItem
                  disableRipple
                  disableTouchRipple
                  className="columns-menu-footer"
                >
                  {visibleColumns.length} of {availableTechnologies.length} columns visible
                </MenuItem>
              </Select>
            </FormControl>
          </div>
        </div>

        {featuresLoading || categoriesLoading || technologyOptionsLoading ? (
          <div className="table-wrap">
            <div className="comparison-loader">
              <Loader variant={LoaderSizes.large} />
            </div>
          </div>
        ) : (
          <div className="table-wrap">
            <table
              className="comparison-table"
              style={
                visibleColumns.length > 0
                  ? ({ ["--tech-col-count" as string]: visibleColumns.length } as React.CSSProperties)
                  : undefined
              }
            >
              <colgroup>
                <col className="feature-col" />
                {visibleColumns.map((name) => (
                  <col key={name} className="tech-col" />
                ))}
              </colgroup>
              <thead>
                <tr>
                  <th className="feature-col">Features</th>
                  {visibleColumns.map((name) => (
                    <th key={name} className="tech-col">
                      {name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(tableRows.length === 0 || visibleColumns.length === 0) && (
                  <tr>
                    <td colSpan={Math.max(visibleColumns.length + 1, 1)} className="comparison-empty-cell">
                      No feature comparison data available.
                    </td>
                  </tr>
                )}
                {tableRows.map((row, index) => (
                  <tr key={row.id}>
                    <td className="feature-col">
                      <p className="feature-title">{`${index + 1}. ${row.featureName}`}</p>
                      <button
                        type="button"
                        className="description-link"
                        onClick={() => openDescriptionModal(row)}
                      >
                        Description View
                      </button>
                      <p className="scope">
                        <span className="scope-label">Scope:</span>{" "}
                        <span className="scope-value">{row.scopeValue}</span>
                      </p>
                    </td>
                    {visibleColumns.map((name) => {
                      const cell = row.cells.get(name);
                      const support = cell?.support || "-";
                      const requirements = cell?.requirements || [];
                      return (
                        <td key={`${row.id}-${name}`} className="tech-col">
                          {support !== "-" ? (
                            <span className={getStatusClass(support)}>{support}</span>
                          ) : (
                            <span className="status timeline">-</span>
                          )}
                          <p className="requirement-label">Requirement:</p>
                          <p className="requirement-values">
                            {requirements.length > 0 ? (
                              requirements.map((reqId, reqIndex) => (
                                <React.Fragment key={`${row.id}-${name}-${reqId}`}>
                                  {reqIndex > 0 && <span className="requirement-separator"> | </span>}
                                  <button
                                    type="button"
                                    className="requirement-link"
                                    onClick={() => openRequirementModal(reqId)}
                                  >
                                    {formatRequirementId(reqId)}
                                  </button>
                                </React.Fragment>
                              ))
                            ) : (
                              "-"
                            )}
                          </p>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog
        open={isDescriptionModalOpen}
        onClose={() => setIsDescriptionModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "6px",
            boxShadow: "0px 12px 32px rgba(15, 23, 42, 0.18)",
            maxWidth: 760,
          },
        }}
      >
        <div className="benefits-ptv-modal">
          <header className="benefits-ptv-modal-header">
            <div>
              <p className="benefits-ptv-modal-eyebrow">Validation Management</p>
              <h3>View Description</h3>
              <p className="benefits-ptv-modal-subtitle">Review feature description</p>
            </div>
            <button
              type="button"
              className="benefits-ptv-modal-close"
              onClick={() => setIsDescriptionModalOpen(false)}
              aria-label="Close"
            >
              <Iconography icon="xmark" />
            </button>
          </header>
          <DialogContent className="benefits-ptv-modal-content">
            <p className="benefits-description-modal-title">High Level Scenario</p>
            <div
              className="benefits-description-preview"
              dangerouslySetInnerHTML={{
                __html:
                  sanitizeHtml(selectedDescriptions.highLevelScenario || "", modalSanitizeOptions)
                    .trim() || "<p>No description added.</p>",
              }}
            />
            <p className="benefits-description-modal-title">Requirement Description</p>
            <div
              className="benefits-description-preview"
              dangerouslySetInnerHTML={{
                __html:
                  sanitizeHtml(
                    selectedDescriptions.requirementDescription || "",
                    modalSanitizeOptions
                  ).trim() || "<p>No description added.</p>",
              }}
            />
          </DialogContent>
        </div>
      </Dialog>

      <Dialog
        open={isRequirementModalOpen}
        onClose={closeRequirementModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0px 12px 24px rgba(0,0,0,0.12)",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography sx={{ fontWeight: 500, fontSize: "20px" }}>Description</Typography>
          <IconButton
            aria-label="close"
            onClick={closeRequirementModal}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <Iconography icon="xmark" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {requirementModalLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", padding: "24px" }}>
              <Loader variant={LoaderSizes.large} />
            </Box>
          ) : (
            <Box className="requirement-description-modal">
              <Typography className="requirement-description-content">
                {selectedRequirementDescription || "N/A"}
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FeatureComparisonTable;
